import React, { ReactNode, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { getLoadingParams, useListContext } from 'framework/list';
import { DATE_FORMAT, useDateService } from 'framework/utils';
import { Checkbox, EnumDropdown, FormFieldsData, ModalFormButton } from 'framework/form';
import { EmployeeOfficeField, EmployeePositionField } from 'domain/shared/employee';
import { FormattedTextField } from 'framework/ui/field';
import { useComplianceReportService } from 'domain/shared/compliance';
import { mergeComplianceReportFilter } from './complianceReportFilters';

interface FilterRowProps {
  label: string;
  children: ReactNode;
}

const REPORT_LANGUAGE_OPTIONS = {
  en: 'English',
  ru: 'Russian',
};

const FilterRow: React.FC<FilterRowProps> = ({ label, children }) => {
  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
      <Typography component="span" variant="subtitle1">
        {label}:
      </Typography>
      <Box component="span">{children}</Box>
    </Box>
  );
};

const ComplianceReportDownloadButton = () => {
  const { filter, search, sort, data } = useListContext();
  const { downloadPdfReport } = useComplianceReportService();
  const [loading, setLoading] = useState(false);
  const { formatDate } = useDateService();
  const mergedFilter = mergeComplianceReportFilter(filter);

  const hasFilters = mergedFilter && Object.keys(mergedFilter).length > 0;

  const asOfDate = mergedFilter?.asOfDate?.eq;
  const policyTypes = mergedFilter?.['specification:policyTypeInPath']?.in;
  const officeIds = mergedFilter?.officeId?.in;
  const positionIds = mergedFilter?.positionId?.in;

  const handleDownload = async (values: FormFieldsData) => {
    setLoading(true);
    const params = getLoadingParams({ sort, filter: mergedFilter, search });
    params.pageSize = data?.totalElements ?? 30;
    await downloadPdfReport(params, values.isHistoryChecked, values.language || 'en');
    setLoading(false);
    return {};
  };

  return (
    <ModalFormButton
      formTitle={'Are you sure?'}
      disabled={loading}
      submitFormFunc={handleDownload}
      label={loading ? 'Downloading...' : 'Download PDF'}
      getDataFunc={async () => {}}
      initialData={{ language: 'en' }}
    >
      <Stack spacing={2}>
        <Typography>
          {hasFilters
            ? 'Do you really want to download compliance report with next filters:'
            : 'Do you really want to download compliance report without filters?'}
        </Typography>

        {hasFilters && (
          <Stack spacing={1}>
            {asOfDate && (
              <FilterRow label="As of date">
                <FormattedTextField
                  source={asOfDate}
                  value={asOfDate}
                  label=""
                  format={(value: string) => formatDate(value, DATE_FORMAT.SIMPLE)?.trim()}
                />
              </FilterRow>
            )}

            {policyTypes && policyTypes.length > 0 && (
              <FilterRow label="Policy types">{policyTypes.join(', ')}</FilterRow>
            )}

            {officeIds && officeIds.length > 0 && (
              <FilterRow label="Offices">
                <Box component="span" sx={{ display: 'inline-flex', flexWrap: 'wrap', alignItems: 'baseline' }}>
                  {officeIds.map((officeId: string, idx: number) => (
                    <React.Fragment key={officeId}>
                      <EmployeeOfficeField value={Number(officeId)} label="" />
                      {idx < officeIds.length - 1 && ', '}
                    </React.Fragment>
                  ))}
                </Box>
              </FilterRow>
            )}

            {positionIds && positionIds.length > 0 && (
              <FilterRow label="Positions">
                <Box component="span" sx={{ display: 'inline-flex', flexWrap: 'wrap', alignItems: 'baseline' }}>
                  {positionIds.map((positionId: string, idx: number) => (
                    <React.Fragment key={positionId}>
                      <Box component="span" sx={{ display: 'inline' }}>
                        <EmployeePositionField value={Number(positionId) as any} label="" />
                      </Box>
                      {idx < positionIds.length - 1 && ', '}
                    </React.Fragment>
                  ))}
                </Box>
              </FilterRow>
            )}
          </Stack>
        )}

        <Box sx={{ mt: 2, width: 200, maxWidth: '100%' }}>
          <EnumDropdown source="language" label="Language" options={REPORT_LANGUAGE_OPTIONS} isClearable={false} />
        </Box>

        <Box>
          <Checkbox source="isHistoryChecked" label="Include policy history in the report" />
        </Box>
      </Stack>
    </ModalFormButton>
  );
};

export default ComplianceReportDownloadButton;
